/* eslint-disable @typescript-eslint/await-thenable */
import { Field, Form, Formik, FormikProps } from 'formik'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { FacebookLogin } from 'src/common/components/FacebookLogin'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { facebookLogin, getAthlete, useSignInMutation } from 'src/services/mainAPI'
import { wrapper } from 'src/store'
import { useAppSelector } from 'src/store/hooks'
import * as Yup from 'yup'

const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    password: Yup.string().required('Password is required'),
    token: Yup.string()
})

const Login = () => {
    const loginFormRef = useRef<FormikProps<any>>(null)
    const dispatch = useDispatch()
    const router = useRouter()
    const [signIn, { error }] = useSignInMutation()
    const user = useAppSelector((state) => state.user.data)

    const handleFBLogin = (data) => {
        dispatch(
            facebookLogin.initiate({
                email: data.profile.email,
                username: data.profile.email,
                firstName: data.profile.first_name,
                lastName: data.profile.last_name,
                ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                facebookUserId: data.profile.id,
                fbAccessToken: data.tokenDetail.accessToken
            })
        )
    }

    useEffect(() => {
        if (user) {
            dispatch(getAthlete.initiate(user.userName))
            localStorage.setItem('user:profile', JSON.stringify(user))

            if (router.query?.redirect) {
                void router.replace(router.query?.redirect as string)
            } else {
                void router.replace('/dashboard-new')
            }
        }
    }, [user])

    return (
        <AppLayout pageTitle="Login">
            <div className="min-h-screen pr-0 md:pr-24 smMd:pt-28 relative">
                <img
                    alt="Login background image"
                    src="https://res.cloudinary.com/yes-fit/image/upload/q_auto/v1636033778/manual/login-yes-fit.svg"
                    className="md:mt-0 md:absolute md:top-32 md:left-0 md:bottom-0 md:right-0 w-full "
                    style={{ zIndex: -1 }}
                />
                {/* Form section */}
                <Formik
                    enableReinitialize
                    validateOnMount={false}
                    innerRef={loginFormRef}
                    initialValues={{
                        email: '',
                        password: ''
                    }}
                    validateOnBlur
                    validationSchema={LoginSchema}
                    onSubmit={async (values) => {
                        await signIn(values)

                        // alert(JSON.stringify(values, null, 2))
                    }}
                >
                    {({ isSubmitting, errors }) => (
                        <Form>
                            <div className="bg-white smMd:shadow-sm smMd:rounded-lg smMd:max-w-xl mr-auto md:mr-0 smMd:ml-auto z-10 block">
                                <div className="p-8 pt-2 md:pt-8 smMd:p-16">
                                    <div className="grid grid-cols-1 gap-4 md:gap-6">
                                        <h1 className="font-medium text-4xl">
                                            Welcome back to Yes.Fit
                                        </h1>

                                        <p className="font-light mb-4">
                                            Please enter your credentials below to sign in
                                            to our site and view your dashboard.
                                        </p>

                                        <Field name="email">
                                            {({
                                                field, // { name, value, onChange, onBlur }
                                                meta
                                            }) => (
                                                <label className="block">
                                                    <input
                                                        disabled={isSubmitting}
                                                        autoComplete="username"
                                                        type="email"
                                                        placeholder="Email"
                                                        className="disabled:bg-gray-200 disabled:opacity-50 mt-1 block w-full rounded-md py-4 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400"
                                                        {...field}
                                                    />
                                                    {meta.touched && meta.error && (
                                                        <span className="text-red-500 text-sm">
                                                            {meta.error}
                                                        </span>
                                                    )}
                                                </label>
                                            )}
                                        </Field>

                                        <Field name="password">
                                            {({
                                                field, // { name, value, onChange, onBlur }
                                                meta
                                            }) => (
                                                <label className="block">
                                                    <input
                                                        disabled={isSubmitting}
                                                        autoComplete="current-password"
                                                        type="password"
                                                        placeholder="Password"
                                                        className="disabled:bg-gray-200 disabled:opacity-50 mt-1 block w-full rounded-md py-4 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400"
                                                        {...field}
                                                    />
                                                    {meta.touched && meta.error && (
                                                        <span className="text-red-500 text-sm">
                                                            {meta.error}
                                                        </span>
                                                    )}
                                                </label>
                                            )}
                                        </Field>

                                        {error && (
                                            <div className="block">
                                                <p className="text-red-500 font-medium">
                                                    {error}
                                                </p>
                                            </div>
                                        )}

                                        {errors && errors.token && (
                                            <div className="block">
                                                <p className="text-red-500 font-medium">
                                                    {errors.token}
                                                </p>
                                            </div>
                                        )}

                                        <div className="block text-right">
                                            <a
                                                className="text-yf-purple text-base font-normal cursor-pointer"
                                                href="/forgot-password"
                                            >
                                                Forgot Password?
                                            </a>
                                        </div>

                                        <div className="flex flex-col text-center mb-4">
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className="mb-3 py-5 bg-yf-green text-white font-medium rounded-lg focus:outline-none focus:ring-2 ring-yf-green-400"
                                            >
                                                {isSubmitting ? 'Loading...' : 'Sign In'}
                                            </button>

                                            <div className="my-1" />

                                            <FacebookLogin
                                                onSuccess={(res) => handleFBLogin(res)}
                                                onError={(error) => alert(error)}
                                            />
                                            <p className="text-xs mt-3">
                                                By clicking on the Sign In or Login with
                                                Facebook button, you agree to the{' '}
                                                <Link href="/terms">
                                                    <a className="text-yf-green">
                                                        terms and conditions
                                                    </a>
                                                </Link>
                                            </p>
                                        </div>

                                        <div className="text-center">
                                            <p>
                                                Don&apos;t have an account?{' '}
                                                <Link href="/register">
                                                    <a className="text-yf-green">
                                                        Sign up
                                                    </a>
                                                </Link>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </AppLayout>
    )
}

Login.getInitialProps = wrapper.getInitialPageProps(() => () => null)

export default Login
